import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { DefaultLayoutComponent } from './containers';
import { LoginComponent } from './views/pages/login/login.component';
import { AuthCallbackComponent } from './views/pages/login/auth-callback.component';
import { OrgNotFoundComponent } from './views/pages/org-not-found/org-not-found.component';
import { NotFoundComponent } from './views/pages/not-found/not-found.component';
import { AuthGuard } from '@auth0/auth0-angular';
import { isAccountSelectedGuard, accountContextGuard } from './services/session/is-group-selected-guard';
import { orgContextGuard } from './services/session/org-context.guard';
import { roleGuard } from './services/session/role.guard';
import { environment } from '../environments/environment';

const routes: Routes = [
    // ── Kitchen Sink (dev only — blocked in production builds) ───────────────
    {
        path: 'kitchen-sink',
        canMatch: [() => !environment.production],
        loadComponent: () =>
            import('./views/kitchen-sink/kitchen-sink.component')
                .then((m) => m.KitchenSinkComponent),
    },
    // ── Debug Settings (local only — excluded from all deployed builds) ─────────
    {
        path: 'settings',
        canMatch: [() => environment.local],
        loadComponent: () =>
            import('./views/debug-settings/debug-settings.component')
                .then((m) => m.DebugSettingsComponent),
    },
    {
        path: '',
        component: LoginComponent,
        pathMatch: 'full',
    },
    // ── Global admin routes (ROOT_ADMIN only) ─────────────────────────────────
    {
        path: '',
        component: DefaultLayoutComponent,
        canActivateChild: [AuthGuard, isAccountSelectedGuard],
        children: [
            {
                path: 'accounts',
                loadChildren: () =>
                    import('./views/accounts/accounts.module').then((m) => m.AccountsModule),
            },
        ],
    },
    // ── Account-scoped routes  /accounts/:account_id/<feature> ───────────────
    {
        path: 'accounts/:account_id',
        component: DefaultLayoutComponent,
        canActivateChild: [AuthGuard, accountContextGuard],
        children: [
            {
                path: 'organizations',
                loadComponent: () =>
                    import('./views/system-admin/organization-list/organization-list.component')
                        .then((m) => m.OrganizationListComponent),
            },
            {
                path: 'organizations/:id/users',
                loadComponent: () =>
                    import('./views/shared/user-list/user-list.component')
                        .then((m) => m.UserListComponent),
                data: { userRole: 'SYSTEM_ADMIN', listType: 'organization', lessColumns: true },
            },
            {
                path: 'organizations/:id/settings',
                loadComponent: () =>
                    import('./views/accounts/organization-settings/organization-settings.component')
                        .then((m) => m.OrganizationSettingsComponent),
            },
            {
                path: 'users',
                loadComponent: () =>
                    import('./views/shared/user-list/user-list.component')
                        .then((m) => m.UserListComponent),
                data: { userRole: 'SYSTEM_ADMIN', listType: 'account' },
            },
            {
                path: 'api-keys',
                loadComponent: () =>
                    import('./views/accounts/api-keys/api-keys.component')
                        .then((m) => m.ApiKeysComponent),
            },
            {
                path: 'api-keys/:key_id',
                loadComponent: () =>
                    import('./views/accounts/api-keys/api-key-detail.component')
                        .then((m) => m.ApiKeyDetailComponent),
            },
            {
                path: 'system-admins',
                loadComponent: () =>
                    import('./views/shared/user-list/user-list.component')
                        .then((m) => m.UserListComponent),
                canActivate: [roleGuard],
                data: { subject: 'SystemAdminsPage', lessColumns: true },
            },
        ],
    },
    {
        path: 'login',
        component: AuthCallbackComponent,
    },
    {
        path: 'org-not-found',
        component: OrgNotFoundComponent,
    },
    // ── Org-scoped routes  /orgs/:org_id/<feature> ────────────────────────────
    {
        path: 'orgs/:org_id',
        component: DefaultLayoutComponent,
        canActivateChild: [AuthGuard, orgContextGuard],
        children: [
            {
                path: '',
                loadChildren: () =>
                    import('./views/organization-data/organization-data.module').then(
                        (m) => m.OrganizationDataModule,
                    ),
            },
            {
                path: 'integrations',
                loadChildren: () =>
                    import('./views/integrations/integrations.module').then((m) => m.IntegrationsModule),
            },
            {
                path: 'connectors',
                loadChildren: () =>
                    import('./views/connectors/connectors.module').then((m) => m.ConnectorsModule),
            },
            {
                path: 'models',
                loadChildren: () =>
                    import('./views/models/models.module').then((m) => m.ModelsModule),
            },
            {
                path: 'map',
                loadChildren: () => import('./views/map/map.module').then((m) => m.MapModule),
            },
            {
                path: 'organization-admin',
                loadChildren: () =>
                    import('./views/organization-admin/organization-admin.module').then(
                        (m) => m.OrganizationAdminModule,
                    ),
            },
            {
                path: 'permissions',
                loadChildren: () =>
                    import('./views/permissions/permissions.module').then((m) => m.PermissionsModule),
            },
        ],
    },
    { path: '**', component: NotFoundComponent },
];

@NgModule({
    imports: [
        RouterModule.forRoot(routes, {
            scrollPositionRestoration: 'top',
            anchorScrolling: 'enabled',
            initialNavigation: 'enabledBlocking',
        }),
    ],
    exports: [RouterModule],
})
export class AppRoutingModule {}
